import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { IconModule } from 'ish-core/icon.module';
import { SortableAttributesType } from 'ish-core/models/product-listing/product-listing.model';
import { SelectOption } from 'ish-core/models/select-option/select-option.model';
import { ViewType } from 'ish-core/models/viewtype/viewtype.types';
import { ProductListPagingComponent } from 'ish-shared/components/product/product-list-paging/product-list-paging.component';

@Component({
  selector: 'ish-product-list-toolbar',
  templateUrl: './product-list-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ProductListPagingComponent, TranslateModule, NgbDropdownModule, RouterModule, IconModule],
})
export class ProductListToolbarComponent implements OnChanges {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translate = inject(TranslateService);

  readonly sortableAttributes = input.required<SortableAttributesType[]>();
  readonly itemCount = input<number>(undefined);
  readonly viewType = input<ViewType>('grid');
  readonly sortBy = input('default');
  readonly currentPage = input<number>(undefined);
  readonly pageIndices = input<
    {
      value: number;
      display: string;
    }[]
  >(undefined);
  readonly fragmentOnRouting = input<string>(undefined);
  readonly isPaging = input(false);
  sortBySignal = signal('default');
  productSortSelected = output<SelectOption>();

  sortOptions: SelectOption[] = [];
  selectedSortLabel: string;

  ngOnChanges() {
    this.sortBySignal.set(this.sortBy());
    this.sortOptions = this.mapSortableAttributesToSelectOptions(this.sortableAttributes());
    const selected = this.sortOptions.find(opt => opt.value === this.sortBySignal());
    if (selected) {
      this.selectedSortLabel = selected.label;
    } else {
      this.selectedSortLabel = this.translate.instant('product.items.sorting.default.label');
    }
  }

  changeSortBy(target: EventTarget) {
    const sorting = (target as HTMLDataElement).value;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
      queryParams: this.isPaging() ? { sorting, page: 1 } : { sorting },
      fragment: this.fragmentOnRouting(),
    });
  }

  onSelectSort(option: SelectOption) {
    if (option.value !== 'default' && option.value === this.sortBySignal()) {
      return;
    }

    this.sortBySignal.set(option.value);
    this.productSortSelected.emit(option);

    const queryParams: any = this.isPaging() ? { page: 1 } : {};

    if (option.value !== 'default') {
      queryParams.sorting = option.value;
    } else {
      queryParams.sorting = null;
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
      queryParams,
      fragment: this.fragmentOnRouting(),
    });
  }

  private mapSortableAttributesToSelectOptions(sortableAttributes: SortableAttributesType[] = []): SelectOption[] {
    const options = sortableAttributes
      .filter(x => !!x)
      .map(sk => ({ value: sk.name, label: sk.displayName || sk.name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    options.unshift({
      value: 'default',
      label: this.translate.instant('product.items.sorting.default.label'),
    });

    return options;
  }

  get listView() {
    return this.viewType() === 'list';
  }

  get gridView() {
    return !this.listView;
  }
}
